import { command } from "../../app/command";
import { query } from "../../app/query";
import { api } from "../../app/api";
import { EventPayload } from "../../common/types/Event";
import { Nullable } from "../../common/types/nullable";
import { Options } from "../../common/types/requestOptions";
import { Paginated } from "./../../common/types/Paginated";
import { Approval } from "./types";
import { AddEntry } from "./types/commands";
import { WorkEntryAdded } from "./types/events";

export type WorkTimeEntryType = "HourlyWork" | "Absence";

export type CollapsibleEntry = {
  [k: string]: string;
};

export interface Expense {
  expenseTypeCode: string;
  unit: string;
  amount: string;
  unitCost: string | null;
  total: string;
}

export interface LunchBreak {
  start: number; // EpochInMs
  duration: number; // Minutes
}

export interface DefaultLunchBreak {
  startDelta: number; // Relative to start of the shift in minutes
  length: number; // Minutes
  setAutomaticallyAfter?: number; // Minutes
}

// TypeScript Interface for WorktimeEntryFeedbackDetails
interface WorktimeEntryFeedbackDetails {
  comment?: string | null;
  like?: TalentLike | null;
  likeComment?: string | null;
  timestamp?: number | null;
}

// TypeScript Enum for TalentLike
enum TalentLike {
  YES = "YES",
  NO = "NO",
}

export interface ApprovalStateChange {
  approvalState: string;
  timestamp: number;
  userFirstName: string;
  userId: string;
  userLastName: string;
  userType: string;
  comment: string;
}

export enum LeaveAttachmentAction {
  UPLOADED = "UPLOADED",
  DELETED = "DELETED",
}

export interface WorkTimeEntryChangeDetails {
  type: string;
  start: number;
  end: number;
  customerCostCenterId: string;
  customerCostCenterName: string;
  supplements: Supplement[];
  lunchBreak: LunchBreak | null;
  leaveId?: string | null;
  maxHoursToBank?: string | null;
  leaveAttachmentAction?: Nullable<LeaveAttachmentAction>;
}

export interface EntryChange
  extends Omit<ApprovalStateChange, "approvalState" | "comment"> {
  details?: WorkTimeEntryChangeDetails;
}

export interface Entry {
  [key: string]: any;

  created: EpochTimeStamp;
  id: string;
  bundleId?: string;
  assignmentId?: string;
  isBillable?: boolean;
  isShift: boolean;
  isTargetedShift?: boolean;
  customerCostCenterId: string;
  customerOrgName: string;
  talentFirstName: string;
  talentLastName: string;
  talentId: string;
  start: EpochTimeStamp;
  end: EpochTimeStamp;
  workTitleId: string;
  workLocationName: string;
  supplements: Supplement[];
  expenses: { [key: string]: Expense };
  comment: string | null;
  customerCostCenterName: string;
  approvalState: Approval;
  approvalStateChanges: ApprovalStateChange[];
  type: string;
  version: number;
  lunchBreak: LunchBreak | null;
  leaveId: string | null;
  plannedStart: EpochTimeStamp;
  feedback: WorktimeEntryFeedbackDetails | null;
  entryChanges: EntryChange[];
  maxHoursToBank?: string | null;
  leaveAttachmentRequired?: boolean;
  leaveAttachment?: { attachments?: Attachment[] };
}

export type NewEntryBase = {
  created: EpochTimeStamp;
  checked: boolean;
  id: string;
  assignmentId: string;
  approvalState: string;
  start: EpochTimeStamp;
  end: EpochTimeStamp;
  version: number;
  customerOrgId: string;
  customerOrgName: string;
  customerCostCenterId: string;
  customerCostCenterName: string;
  leaveId: string | null;
  isShift: boolean;
  type: string;
  defaultLunchBreak: DefaultLunchBreak | null;
  lunchBreak: LunchBreak | null;
  plannedStart: EpochTimeStamp;
  feedback: WorktimeEntryFeedbackDetails | null;
  talentId: string;
  talentFirstName: string;
  talentLastName: string;
  expenses: { [key: string]: Expense };
  isBillable: boolean;
  workLocationName: string;
  workLocationId: string;
  workTitleId: string;
  approvalStateChanges: ApprovalStateChange[];
  entryChanges: EntryChange[];
  supplements: Supplement[];
  comment: string;
};

export interface EntryResponse {
  data: Entry[];
  cursor: string;
}

export interface EditEntry {
  approvalState: Approval;
  created: EpochTimeStamp;
  id: string;
  bundleId?: string;
  isTargetedShift?: boolean;
  version: number;
  type: string;
  customerOrgId: string;
  customerOrgName: string;
  customerCostCenterId: string;
  customerCostCenterName: string;
  leaveId?: string | null;
  talentId: string;
  talentFirstName: string;
  talentLastName: string;
  start: number;
  end: number;
  expenses: { [key: string]: Expense };
  isBillable: boolean;
  isShift?: boolean;
  workTitleId: string;
  workLocationName: string;
  comment?: string | null;
  lunchBreak: LunchBreak | null;
  defaultLunchBreak: DefaultLunchBreak | null;
  assignmentId: string;
  supplements: Supplement[];
  plannedStart: number;
  feedback: WorktimeEntryFeedbackDetails | null;
  approvalStateChanges: ApprovalStateChange[];
  workLocationId: string;
  entryChanges: EntryChange[];
  maxHoursToBank?: string | null;
  flexibleHours?: boolean;
}

export interface LeaveEntry {
  id: string;
  version: number;
  type: string;
  startDate: string;
  endDate: string;
  continuationOf: {
    id: string;
    type: string;
    startDate: string;
    endDate: string;
  };
  entries: Entry[];
}

export interface Supplement {
  code: string;
  amount?: string | null;
  time?: {
    start: number;
    end: number;
  } | null;
  description?: string | null;
}

export interface NewEntryTalentData {
  id: string;
  firstName: string;
  lastName: string;
}

export interface PostEditEntry {
  id: string;
  version: number;
  type: string;
  start: number;
  end: number;
  isBillable: boolean;
  customerCostCenterId: string;
  comment?: string | null;
  supplements: Supplement[];
  lunchBreak: LunchBreak | null;
  markApproved: boolean;
  leaveId?: string | null;
  maxHoursToBank?: string | null;
  flexibleHours?: boolean;
}

export interface PostEditEntryResponse {
  id: string;
  version: number;
  name: string;
  details: {
    customerCostCenterId: string;
    type: string;
    start: number;
    end: number;
    lunchBreak: LunchBreak | null;
  };
  markApproved: boolean;
}

export interface EntryFilters {
  approval?: string;
  sort: string;
  pageSize: string;
  rangeStart?: string;
  rangeEnd?: string;
  cursor?: string;
  type?: string;
  talentId?: string;
  isZeroExpenseEntry?: string;
  customerOrgId?: string;
  customerCompanyId?: string;
  tesId?: string;
  hasComment?: string;
  eezyCompanyId?: string;
  workLocationIds?: string[];
  isOnPayslip?: string;
  isOnInvoice?: string;
  hrRepresentativeIds?: string[];
}

export interface UpdatableEntry {
  id: string;
  version: number;
}

export interface PostDeleteEntry {
  id: string;
  version: number;
}

export type Leave = {
  id: string; // Id of this sick leave
  type: string; // Type of the leave
  startDate: string; // Leave start date
  endDate: string; // Leave end date
};

export type ContinuedLeave = Leave & {
  continuationOf: string | undefined; // Id of the leave this leave continues from
};

export type LeaveMap = {
  order: string[]; // rootIds in desired order
  chains: { [rootId: string]: ContinuedLeave[] };
};

/**
 * Removes a rootId and its associated chain from the LeaveMap.
 * @param leaveMap The LeaveMap object to modify.
 * @param rootId The rootId to remove.
 * @returns The updated LeaveMap.
 */
export const removeRootIdFromLeaveMap = (
  leaveMap: LeaveMap,
  rootId: string,
): LeaveMap => {
  const { order, chains } = leaveMap;

  // Remove the rootId from the order array
  const updatedOrder = order.filter((id) => id !== rootId);

  // Remove the rootId from the chains object
  const { [rootId]: _, ...updatedChains } = chains;

  return {
    order: updatedOrder,
    chains: updatedChains,
  };
};

export type LeaveFromTo = {
  talentId: string; // Id of the talent
  type: string; // Type of the leave
  from: string; // Leave start date
  to: string; // Leave end date
  continuationOf?: string; // Id of the leave this leave continues from if applicable
};

interface Continuation {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
}

export interface Attachment {
  filename: string;
  pathspec: string;
  size: number;
}

export interface LeaveEntityDateRange {
  startDate: string | null;
  endDate: string | null;
}

export interface LeaveEntity {
  id: string | null;
  version: number;
  talentId: string | null;
  type: string;
  comment?: string | null;
  dateRange: LeaveEntityDateRange;
  continuationOf: Continuation | null;
  continuationOfId: string | null;
  entries: Entry[];
  previousLeaves: LeaveMap;
  leaveAttachment?: {
    attachments?: Attachment[];
  };
}

export interface ModifyAbsenceEntry {
  entryId: string;
  approve: boolean;
  start: number;
  end: number;
  lunchBreak: LunchBreak | null;
}

export interface ModifyAbsenceNewEntry {
  assignmentId: string;
  approve: boolean;
  start: number;
  end: number;
  customerCostCenterId: string;
  lunchBreak: LunchBreak | null;
}

export interface PostModifyAbsence {
  absenceId?: string;
  talentId?: string;
  version?: number;
  type: string;
  startDate: string;
  endDate: string;
  continuationOf: string | null;
  entries: ModifyAbsenceEntry[];
  newEntries: ModifyAbsenceNewEntry[];
}

export interface RemoveLeaveAttachment {
  filename: string;
  id: string;
}

export interface LeaveAttachmentDownloadUrl {
  url: string;
}

export interface LeaveAttachmentDownloadUrlQuery {
  pathspec: string;
  talentId: string;
  leaveId: string;
}

export interface LeaveAttachmentUploadUrlQuery {
  talentId: string;
  leaveId: string;
  filename: string;
  size: string;
}

export interface LeaveAttachmentUploadUrl {
  url: string;
}

export interface WrkEntAddEvent {
  id: string;
  name: string;
  payload: {
    assignmentId: string;
    details: {
      customerCostCenterId: string;
      start: number;
      end: number;
      supplements: Supplement[];
      type: string;
    };
    splitFrom: string;
  };
  version: number;
}

export type EntryApprovalStateReset = {
  newState: "New";
};

export const listEntries = (filters: EntryFilters, { requestId }: Options) => {
  let params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value.toString()) {
      params[key] = value.toString();
    }
  }

  return api
    .get<Paginated<Entry[]>>("/projections/qry/worktimeEntries", {
      requestId,
      queryParams: {
        ...params,
      },
      auth: true,
    })
    .then((res) => res.data);
};

export const getEntryById = (entryId: string, { requestId }: Options) => {
  return api
    .get<EditEntry>(`/projections/qry/worktimeEntry`, {
      requestId,
      queryParams: {
        entryId,
      },
      auth: true,
    })
    .then((res) => {
      return {
        ...res,
      };
    });
};

export const getLeaveEntryById = (leaveId: string, { requestId }: Options) => {
  return api
    .get<LeaveEntry>(`/projections/qry/leaveById`, {
      requestId,
      queryParams: {
        leaveId,
      },
      auth: true,
    })
    .then((res) => {
      return {
        ...res,
      };
    });
};

export const getAbsencesById = (
  absenceFromTo: LeaveFromTo,
  { requestId }: Options,
) => {
  return api
    .get<ContinuedLeave[]>(`/projections/qry/absences`, {
      requestId,
      queryParams: {
        ...absenceFromTo,
      },
      auth: true,
    })
    .then((res) => {
      return {
        ...res,
      };
    });
};

export const modifyAbsence = async (
  leaveEntity: PostModifyAbsence,
  { requestId }: Options,
): Promise<any> => {
  return await api
    .post<any>("/working/cmd/modifyAbsence", {
      requestId,
      auth: true,
      body: {
        ...leaveEntity,
      },
    })
    .then((res) => {
      return res;
    });
};

export const deleteEntry = async (
  postDeleteEntry: PostDeleteEntry,
  { requestId }: Options,
): Promise<any> => {
  return await api
    .post<any>("/working/cmd/deleteEntry", {
      requestId,
      auth: true,
      body: {
        ...postDeleteEntry,
      },
    })
    .then((res) => {
      return res;
    });
};

export const postEditEntry = async (
  entry: PostEditEntry,
  { requestId }: Options,
): Promise<any> => {
  return await api
    .post<any>("/working/cmd/editEntry", {
      requestId,
      auth: true,
      body: {
        id: entry.id,
        version: entry.version,
        details: {
          type: entry.type,
          start: entry.start,
          end: entry.end,
          customerCostCenterId: entry.customerCostCenterId,
          lunchBreak: entry.lunchBreak,
          comment: entry.comment,
          leaveId: entry.leaveId || null,
          supplements: entry.supplements,
          maxHoursToBank: entry.maxHoursToBank,
          flexibleHours: entry.flexibleHours,
        },
        markApproved: entry.markApproved,
        isBillable: entry.isBillable,
      },
    })
    .then((res) => {
      // Find correct events from possibly multiple objects in array
      const matchingEntries = res.data.filter(
        (item: any) => item.id === entry.id,
      );
      return matchingEntries;
    });
};

// placeholder
export const approveEntries = (
  params: UpdatableEntry[],
  { requestId }: Options,
) => {
  return api
    .post<any>("/working/cmd/approveEntries", {
      requestId,
      auth: true,
      body: {
        entries: [...params],
      },
    })
    .then((res) => res.data);
};

// placeholder
export const confirmEntriesRejected = (
  params: UpdatableEntry[],
  { requestId }: Options,
) => {
  return api
    .post<any>("/working/cmd/confirmEntriesRejected", {
      requestId,
      auth: true,
      body: {
        entries: [...params],
      },
    })
    .then((res) => res.data);
};

export const leaveAttachmentDownloadUrl = (
  queryParams: LeaveAttachmentDownloadUrlQuery,
  { requestId }: Options,
): Promise<LeaveAttachmentDownloadUrl> => {
  return api
    .get<LeaveAttachmentDownloadUrl>(
      "/working/qry/leaveAttachmentDownloadUrl",
      {
        auth: true,
        requestId,
        queryParams: {
          ...queryParams,
        },
      },
    )
    .then((res) => res.data);
};

export const leaveAttachmentUploadUrl = (
  queryParams: LeaveAttachmentUploadUrlQuery,
  options: Options = {},
): Promise<LeaveAttachmentUploadUrl> =>
  query<LeaveAttachmentUploadUrlQuery, LeaveAttachmentUploadUrl>(
    "/working/qry/leaveAttachmentUploadUrl",
    queryParams,
    options,
  );

export const uploadLeaveAttachmentToS3 = (
  url: string,
  file: File,
): Promise<void> =>
  api
    .upload(url, {
      headers: {
        "Content-Type": file.type,
        "Content-Length": String(file.size),
      },
      body: file,
    })
    .then(() => undefined);

export const removeLeaveAttachment = (
  body: RemoveLeaveAttachment,
  { requestId }: Options,
): Promise<any> => {
  return api
    .post<any>("/working/cmd/removeLeaveAttachment", {
      auth: true,
      requestId,
      body,
    })
    .then((res) => res.data);
};

export const addEntry = (
  body: AddEntry,
  { requestId }: Options,
): Promise<EventPayload<WorkEntryAdded>[]> =>
  api
    .post<EventPayload<WorkEntryAdded>[]>("/working/cmd/addEntry", {
      auth: true,
      requestId,
      body,
    })
    .then((res) => res.data);

export const resetEntryApprovalStateCmd = (
  entries: UpdatableEntry[],
  options: Options = {},
) =>
  command<{ entries: UpdatableEntry[] }, EntryApprovalStateReset>(
    "/working/cmd/resetEntryApproval",
    { entries },
    options,
  );
